/* Gestão de FAQ/capacidades no backoffice (ADR-0658 + ADR-0720). Visão POR-TÓPICO (slug): a lista é
   PAGINADA POR TÓPICO no servidor (keyset categoryKey+sortOrder+slug) — cada página traz tópicos com
   todas as traduções; o form edita o tópico com abas por idioma. Cria/edita/remove (super_admin+
   support). Conteúdo é lido no app na aba de config (só `published`, por locale).
   FILTROS (busca/status/categoria/idioma): permanecem CLIENT-SIDE sobre as páginas JÁ CARREGADAS
   (trade-off ADR-0720). Movê-los para o servidor sobre o grouping por-tópico (status/categoryLabel
   por-idioma, busca multi-idioma, multi-select) não é trivial → mantidos client-side + "Carregar
   mais" para trazer mais tópicos. Categoria: agrupa/filtra por `categoryKey` (chave estável) e exibe
   o `categoryLabel` do idioma canônico. */
import type { FaqStatus, FaqTopic, StaffRole } from '@planuze/admin-contracts';
import { FAQ_BULK_MAX_TOPICS, FAQ_STATUSES, faqTopicPrimaryLocale } from '@planuze/admin-contracts';
import {
  Button,
  ConfirmDialog,
  InlineAlert,
  Page,
  PageActions,
  PageContent,
  PageDescription,
  PageHeader,
  PageTitle,
  PageTitleBlock,
  TableSkeleton,
  toast,
} from '@planuze/ui';
import { useMemo, useState } from 'react';
import { FaqBulkBar } from '../components/faq/faq-bulk-bar.js';
import { FaqFormModal } from '../components/faq/faq-form-modal.js';
import { FaqTable } from '../components/faq/faq-table.js';
import { FacetMultiSelect } from '../components/filters/facet-multi-select.js';
import { SegmentedControl, type SegmentOption } from '../components/filters/segmented-control.js';
import { SearchInput } from '../components/search-input.js';
import { useFaqContent } from '../content/sections/faq.js';
import { useBulkDeleteFaqTopics, useDeleteFaqTopic, useFaqTopics } from '../hooks/use-faq.js';
import { useLimitedBulkSelection } from '../hooks/use-limited-bulk-selection.js';
import { useStaffSession } from '../hooks/use-staff-session.js';

const MANAGE_ROLES: readonly StaffRole[] = ['super_admin', 'support'];

/* Um tópico casa o filtro de status se QUALQUER idioma estiver naquele status (status é
   por-idioma). */
type FaqStatusFilter = 'all' | FaqStatus;

function topicHasStatus(topic: FaqTopic, status: FaqStatus): boolean {
  return topic.locales.some((l) => l.status === status);
}

export function FaqPage() {
  const copy = useFaqContent();
  const session = useStaffSession();
  const canManage = session !== null && MANAGE_ROLES.includes(session.role);
  const { data, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useFaqTopics();
  const del = useDeleteFaqTopic();
  const bulkDelete = useBulkDeleteFaqTopics();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<FaqTopic | undefined>(undefined);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<FaqStatusFilter>('all');
  const [categories, setCategories] = useState<string[]>([]);
  const [locales, setLocales] = useState<string[]>([]);
  /* Confirmação destrutiva via Dialog (§5/§8) — remove TODAS as linhas de idioma do tópico. */
  const [toDelete, setToDelete] = useState<FaqTopic | null>(null);
  const [pendingBulkSlugs, setPendingBulkSlugs] = useState<readonly string[] | null>(null);

  const openCreate = (): void => {
    setEditing(undefined);
    setModalOpen(true);
  };
  const openEdit = (topic: FaqTopic): void => {
    setEditing(topic);
    setModalOpen(true);
  };

  /* Tópicos já vêm agrupados/ordenados do servidor; achata as páginas carregadas. */
  const topics = useMemo(() => data?.pages.flatMap((page) => page.items) ?? [], [data]);

  /* Categoria: opções derivadas (dedup por categoryKey) das páginas carregadas; o rótulo exibido é o
     categoryLabel do idioma canônico. O contrato não tem enum fixo de categorias. */
  const categoryOptions = useMemo(() => {
    const byKey = new Map<string, string>();
    for (const topic of topics) {
      if (!byKey.has(topic.categoryKey))
        byKey.set(topic.categoryKey, faqTopicPrimaryLocale(topic).categoryLabel);
    }
    return [...byKey.entries()]
      .sort((a, b) => a[1].localeCompare(b[1], 'pt-BR'))
      .map(([value, label]) => ({ value, label }));
  }, [topics]);
  const localeValues = useMemo(
    () =>
      [...new Set(topics.flatMap((t) => t.locales.map((l) => l.locale)))].sort((a, b) =>
        a.localeCompare(b),
      ),
    [topics],
  );
  const showLocaleFacet = localeValues.length > 1;

  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const s of FAQ_STATUSES) counts[s] = topics.filter((t) => topicHasStatus(t, s)).length;
    return counts;
  }, [topics]);
  const statusOptions: readonly SegmentOption<FaqStatusFilter>[] = [
    { value: 'all', label: copy.allStatusesLabel, count: topics.length },
    ...FAQ_STATUSES.map((s) => ({
      value: s,
      label: copy.statusLabels[s],
      count: statusCounts[s] ?? 0,
    })),
  ];

  const query = search.trim().toLowerCase();
  const filtered = useMemo(
    () =>
      topics.filter((topic) => {
        if (statusFilter !== 'all' && !topicHasStatus(topic, statusFilter)) return false;
        if (categories.length > 0 && !categories.includes(topic.categoryKey)) return false;
        if (locales.length > 0 && !topic.locales.some((l) => locales.includes(l.locale))) {
          return false;
        }
        if (
          query.length > 0 &&
          !topic.categoryKey.toLowerCase().includes(query) &&
          !topic.slug.toLowerCase().includes(query) &&
          !topic.locales.some(
            (l) =>
              l.question.toLowerCase().includes(query) ||
              l.categoryLabel.toLowerCase().includes(query),
          )
        ) {
          return false;
        }
        return true;
      }),
    [topics, statusFilter, categories, locales, query],
  );
  const visibleSlugs = useMemo(() => filtered.map((topic) => topic.slug), [filtered]);
  const selection = useLimitedBulkSelection(visibleSlugs, FAQ_BULK_MAX_TOPICS);
  const selectedTopics = useMemo(
    () => filtered.filter((topic) => selection.selectedIds.has(topic.slug)),
    [filtered, selection.selectedIds],
  );

  const hasFilters =
    query.length > 0 || statusFilter !== 'all' || categories.length > 0 || locales.length > 0;
  const clearFilters = (): void => {
    setSearch('');
    setStatusFilter('all');
    setCategories([]);
    setLocales([]);
  };

  return (
    <Page>
      <PageHeader>
        <PageTitleBlock>
          <PageTitle>{copy.pageTitle}</PageTitle>
          <PageDescription>{copy.pageDescription}</PageDescription>
        </PageTitleBlock>
        {canManage && (
          <PageActions>
            <Button onClick={openCreate}>{copy.newTopicButton}</Button>
          </PageActions>
        )}
      </PageHeader>
      <PageContent className="space-y-4">
        <InlineAlert variant="info">
          {copy.infoIntro}
          <strong>{copy.topicWord}</strong>
          {copy.infoAfterTopic}
          <strong>{copy.categoryKeyWord}</strong>
          {copy.infoAfterCategoryKey}
          <strong>{copy.labelWord}</strong>
          {copy.infoAfterLabel}
          <strong>{copy.publishedWord}</strong>
          {copy.infoAfterPublished}
        </InlineAlert>
        <div className="flex flex-wrap items-center gap-3">
          <SearchInput
            value={search}
            onChange={setSearch}
            ariaLabel={copy.searchAriaLabel}
            placeholder={copy.searchPlaceholder}
          />
          <SegmentedControl
            value={statusFilter}
            onChange={setStatusFilter}
            options={statusOptions}
            ariaLabel={copy.statusFilterAriaLabel}
          />
          <FacetMultiSelect
            label={copy.categoryFacetLabel}
            values={categories}
            onChange={setCategories}
            options={categoryOptions}
          />
          {showLocaleFacet && (
            <FacetMultiSelect
              label={copy.localeFacetLabel}
              values={locales}
              onChange={setLocales}
              options={localeValues.map((l) => ({ value: l, label: l }))}
            />
          )}
        </div>
        {canManage && (
          <FaqBulkBar
            selectedCount={selectedTopics.length}
            busy={bulkDelete.isPending}
            onRemove={() => setPendingBulkSlugs(selectedTopics.map((topic) => topic.slug))}
            onClear={selection.clear}
          />
        )}
        {isError ? (
          <InlineAlert variant="destructive">{copy.loadError}</InlineAlert>
        ) : isLoading ? (
          <TableSkeleton columns={canManage ? 6 : 4} />
        ) : (
          <FaqTable
            items={filtered}
            canManage={canManage}
            onEdit={openEdit}
            onDelete={setToDelete}
            deleting={del.isPending}
            onCreate={openCreate}
            hasFilters={hasFilters}
            onClearFilters={clearFilters}
            selectedSlugs={selection.selectedIds}
            onToggle={selection.toggle}
            onToggleAll={selection.toggleAll}
          />
        )}
        {hasNextPage && (
          <div className="flex justify-center">
            <Button
              variant="ghost"
              loading={isFetchingNextPage}
              onClick={() => {
                fetchNextPage().catch(() => undefined);
              }}
            >
              {copy.loadMoreButton}
            </Button>
          </div>
        )}
      </PageContent>
      {modalOpen && (
        <FaqFormModal open={modalOpen} onClose={() => setModalOpen(false)} topic={editing} />
      )}
      <ConfirmDialog
        open={toDelete !== null}
        onOpenChange={(open) => {
          if (!open) setToDelete(null);
        }}
        title={copy.deleteDialogTitle}
        description={toDelete !== null ? copy.deleteDialogDescription(toDelete.slug) : undefined}
        confirmLabel={copy.deleteConfirmButton}
        variant="destructive"
        onConfirm={async () => {
          const topic = toDelete;
          if (topic === null) return;
          try {
            await del.mutateAsync({ slug: topic.slug, ids: topic.locales.map((l) => l.id) });
            toast.success(copy.deleteSuccessToast);
          } catch {
            toast.error(copy.deleteErrorToast);
          }
          setToDelete(null);
        }}
      />
      <ConfirmDialog
        open={pendingBulkSlugs !== null}
        onOpenChange={(open) => {
          if (!open) setPendingBulkSlugs(null);
        }}
        title={copy.bulk.title}
        description={copy.bulk.description(pendingBulkSlugs?.length ?? 0)}
        confirmLabel={copy.bulk.remove}
        variant="destructive"
        onConfirm={async () => {
          if (pendingBulkSlugs === null || pendingBulkSlugs.length === 0) return;
          const response = await bulkDelete.mutateAsync(pendingBulkSlugs);
          const failed = response.results.filter((result) => !result.ok);
          if (failed.length === 0) toast.success(copy.bulk.success(response.results.length));
          else
            toast.warning(
              copy.bulk.partial(response.results.length - failed.length, failed.length),
            );
          selection.replace(failed.map((result) => result.slug));
          setPendingBulkSlugs(null);
        }}
      />
    </Page>
  );
}
